import { InboxOutlined } from '@ant-design/icons';
import type { ActionType, ProColumns } from '@ant-design/pro-components';
import { ProTable } from '@ant-design/pro-components';
import { useRequest } from 'ahooks';
import {
  Button,
  Checkbox,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Space,
  Upload,
} from 'antd';
import type { RcFile } from 'antd/es/upload';
import { useMemo, useRef, useState } from 'react';
import {
  ExamQuestionDTO,
  ExamQuestionSaveDTO,
  ExamQuestionTypeEnum,
  examQuestionController,
} from '../../api';
import { useCompany } from '../../hooks/company';
import { optionLetter, questionTypeMapping, QuestionTypeTag } from './constants';

const { Dragger } = Upload;

const JUDGE_OPTIONS = ['正确', '错误'];

interface EditModalProps {
  open: boolean;
  question?: ExamQuestionDTO;
  onClose: (refresh: boolean) => void;
}

// 新建/编辑题目弹窗：题型切换动态渲染选项与答案控件
const QuestionEditModal = ({ open, question, onClose }: EditModalProps) => {
  const { id: companyId } = useCompany();
  const [form] = Form.useForm();
  const type = Form.useWatch('type', form) as ExamQuestionTypeEnum | undefined;
  const options = Form.useWatch('options', form) as string[] | undefined;

  const { loading, run: save } = useRequest(
    async () => {
      const values = await form.validateFields();
      const params: ExamQuestionSaveDTO = {
        type: values.type,
        productName: values.productName,
        content: values.content,
        options:
          values.type === ExamQuestionTypeEnum.JUDGE
            ? JUDGE_OPTIONS
            : values.options.filter((item: string) => !!item?.trim()),
        correctAnswer: Array.isArray(values.correctAnswer)
          ? values.correctAnswer.join(',')
          : values.correctAnswer,
      };
      if (question?.id) {
        return examQuestionController.update(companyId, question.id, params);
      }
      return examQuestionController.create(companyId, params);
    },
    {
      manual: true,
      onSuccess: () => {
        message.success('保存成功');
        onClose(true);
      },
      onError: (e) => message.error(e.message),
    },
  );

  const answerOptions = useMemo(() => {
    const list =
      type === ExamQuestionTypeEnum.JUDGE ? JUDGE_OPTIONS : options || [];
    return list
      .map((item, index) => ({
        label: `${optionLetter(index)}. ${item || ''}`,
        value: optionLetter(index),
      }))
      .filter((item, index) => !!(list[index] || '').trim());
  }, [type, options]);

  return (
    <Modal
      title={question ? '编辑题目' : '新建题目'}
      open={open}
      confirmLoading={loading}
      onOk={save}
      onCancel={() => onClose(false)}
      destroyOnClose
      width={640}
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={
          question
            ? {
                type: question.type,
                productName: question.productName,
                content: question.content,
                options: question.options,
                correctAnswer:
                  question.type === ExamQuestionTypeEnum.MULTIPLE
                    ? question.correctAnswer?.split(',')
                    : question.correctAnswer,
              }
            : {
                type: ExamQuestionTypeEnum.SINGLE,
                options: ['', '', '', ''],
              }
        }
      >
        <Form.Item name="type" label="题型" rules={[{ required: true }]}>
          <Radio.Group
            options={Object.entries(questionTypeMapping).map(
              ([value, label]) => ({ value, label }),
            )}
            onChange={() => form.setFieldValue('correctAnswer', undefined)}
          />
        </Form.Item>
        <Form.Item name="productName" label="产品名称">
          <Input placeholder="可选，题目所属产品" maxLength={64} />
        </Form.Item>
        <Form.Item
          name="content"
          label="题干"
          rules={[{ required: true, message: '请输入题干' }]}
        >
          <Input.TextArea rows={3} maxLength={1024} showCount />
        </Form.Item>
        {type !== ExamQuestionTypeEnum.JUDGE && (
          <Form.List name="options">
            {(fields, { add, remove }) => (
              <Form.Item label="选项（2-6 个）" required>
                <Space direction="vertical" style={{ width: '100%' }}>
                  {fields.map((field, index) => (
                    <Space.Compact key={field.key} style={{ width: '100%' }}>
                      <Form.Item
                        {...field}
                        noStyle
                        rules={[{ required: true, message: '选项不能为空' }]}
                      >
                        <Input
                          addonBefore={optionLetter(index)}
                          placeholder={`选项 ${optionLetter(index)}`}
                          maxLength={256}
                        />
                      </Form.Item>
                      {fields.length > 2 && (
                        <Button onClick={() => remove(field.name)}>删除</Button>
                      )}
                    </Space.Compact>
                  ))}
                  {fields.length < 6 && (
                    <Button block type="dashed" onClick={() => add('')}>
                      添加选项
                    </Button>
                  )}
                </Space>
              </Form.Item>
            )}
          </Form.List>
        )}
        <Form.Item
          name="correctAnswer"
          label="正确答案"
          dependencies={['options', 'type']}
          rules={[
            { required: true, message: '请选择正确答案' },
            {
              // 删除选项后旧答案可能越界（如剩 3 个选项但答案还是 D），必须重新校验
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve();
                }
                const currentType = form.getFieldValue('type');
                const validCount =
                  currentType === ExamQuestionTypeEnum.JUDGE
                    ? JUDGE_OPTIONS.length
                    : (form.getFieldValue('options') || []).filter(
                        (item: string) => !!item?.trim(),
                      ).length;
                const letters: string[] = Array.isArray(value)
                  ? value
                  : [value];
                const outOfRange = letters.some(
                  (letter) => letter.charCodeAt(0) - 65 >= validCount,
                );
                return outOfRange
                  ? Promise.reject(
                      new Error('正确答案超出当前选项范围，请重新选择'),
                    )
                  : Promise.resolve();
              },
            },
          ]}
        >
          {type === ExamQuestionTypeEnum.MULTIPLE ? (
            <Checkbox.Group options={answerOptions} />
          ) : (
            <Radio.Group
              options={
                type === ExamQuestionTypeEnum.JUDGE
                  ? JUDGE_OPTIONS.map((item, index) => ({
                      label: `${optionLetter(index)}. ${item}`,
                      value: optionLetter(index),
                    }))
                  : answerOptions
              }
            />
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
};

// 题库 xlsx 导入：列为 题型/产品名称/题干/选项A~F/正确答案，重复题自动跳过
const ImportButton = ({ onFinish }: { onFinish: () => void }) => {
  const { id: companyId } = useCompany();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<RcFile>();

  const { loading, run: doImport } = useRequest(
    () => examQuestionController.importQuestions(companyId, file as RcFile),
    {
      manual: true,
      onSuccess: (data) => {
        Modal.info({
          title: '导入完成',
          content: (
            <div>
              <p>
                共识别 {data.total} 行，导入 {data.imported} 题，重复跳过{' '}
                {data.skipped} 题。
              </p>
              {!!data.errorRows?.length && (
                <div>
                  <p>以下 {data.errorRows.length} 行有误未导入：</p>
                  <ul style={{ maxHeight: 200, overflow: 'auto' }}>
                    {data.errorRows.map((item) => (
                      <li key={item.row}>
                        第 {item.row} 行：{item.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ),
        });
        setOpen(false);
        setFile(undefined);
        onFinish();
      },
      onError: (e) => message.error(e.message),
    },
  );

  return (
    <>
      <Button onClick={() => setOpen(true)}>导入题库</Button>
      <Modal
        title="导入题库"
        open={open}
        confirmLoading={loading}
        okButtonProps={{ disabled: !file }}
        onOk={doImport}
        onCancel={() => {
          setOpen(false);
          setFile(undefined);
        }}
        destroyOnClose
      >
        <p>
          Excel 列依次为：题型（single/multiple/judge）、产品名称、题干、选项
          A~F、正确答案（字母逗号分隔，如 A,B,E）。与库中重复的题会自动跳过。
        </p>
        <Dragger
          accept="application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          name="file"
          multiple={false}
          maxCount={1}
          beforeUpload={(f) => {
            setFile(f);
            return false;
          }}
          onRemove={() => setFile(undefined)}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">选择或拖拽 xlsx 文件到此区域</p>
        </Dragger>
      </Modal>
    </>
  );
};

export default () => {
  const { id: companyId, hasPermission } = useCompany();
  const ref = useRef<ActionType>();
  const [editing, setEditing] = useState<{
    open: boolean;
    question?: ExamQuestionDTO;
  }>({ open: false });

  const canEdit = hasPermission('page:exam:question:edit');

  const { data: products } = useRequest(() =>
    examQuestionController.listProducts(companyId),
  );

  const columns: ProColumns<ExamQuestionDTO>[] = [
    {
      title: '题型',
      dataIndex: 'type',
      width: 100,
      render: (_, record) => <QuestionTypeTag type={record.type} />,
      renderFormItem: () => (
        <Select
          allowClear
          options={Object.entries(questionTypeMapping).map(
            ([value, label]) => ({ value, label }),
          )}
        />
      ),
    },
    {
      title: '产品名称',
      dataIndex: 'productName',
      width: 220,
      renderFormItem: () => (
        <Select
          allowClear
          showSearch
          popupMatchSelectWidth={false}
          options={(products || []).map((item) => ({
            value: item,
            label: item,
          }))}
        />
      ),
    },
    {
      title: '题干',
      dataIndex: 'content',
      ellipsis: true,
      search: {
        transform: (value) => ({ keyword: value }),
      },
    },
    {
      title: '选项',
      dataIndex: 'options',
      hideInSearch: true,
      ellipsis: true,
      render: (_, record) =>
        (record.options || [])
          .map((item, index) => `${optionLetter(index)}.${item}`)
          .join('  '),
    },
    {
      title: '正确答案',
      dataIndex: 'correctAnswer',
      width: 100,
      hideInSearch: true,
    },
    {
      title: '操作',
      valueType: 'option',
      width: 140,
      render: (_, record) =>
        canEdit
          ? [
              <a
                key="edit"
                onClick={() => setEditing({ open: true, question: record })}
              >
                编辑
              </a>,
              <Popconfirm
                key="remove"
                title="确认删除这道题？已建考试的快照不受影响"
                onConfirm={async () => {
                  try {
                    await examQuestionController.remove(
                      companyId,
                      record.id as I.ID,
                    );
                    message.success('删除成功');
                    ref.current?.reload();
                  } catch (e: any) {
                    message.error(e.message);
                  }
                }}
              >
                <a>删除</a>
              </Popconfirm>,
            ]
          : ['-'],
    },
  ];

  return (
    <>
      <ProTable<ExamQuestionDTO>
        actionRef={ref}
        rowKey="id"
        columns={columns}
        pagination={{ pageSizeOptions: [20, 50, 100, 200] }}
        request={async ({ current, pageSize, ...params }) => {
          const data = await examQuestionController.list(
            companyId,
            current,
            pageSize,
            {
              type: (params as any).type,
              productName: (params as any).productName,
              keyword: (params as any).keyword,
            },
          );
          return { data: data.list, success: true, total: data.total };
        }}
        headerTitle="题库管理"
        toolBarRender={() =>
          canEdit
            ? [
                <ImportButton
                  key="import"
                  onFinish={() => ref.current?.reload()}
                />,
                <Button
                  key="create"
                  type="primary"
                  onClick={() => setEditing({ open: true })}
                >
                  新建题目
                </Button>,
              ]
            : []
        }
      />
      {editing.open && (
        <QuestionEditModal
          open={editing.open}
          question={editing.question}
          onClose={(refresh) => {
            setEditing({ open: false });
            if (refresh) {
              ref.current?.reload();
            }
          }}
        />
      )}
    </>
  );
};
